import React, { useState } from "react";
import NavbarConf, { NavbarItem } from "./NavbarConf";
import { useLocation } from "react-router-dom";
import {
  ShieldHalf,
  BanknoteArrowDown,
  FileCheck,
  FileBox,
  ChartArea,
  Settings,
  ClipboardList,
  Goal,
  FilePenLine,
  Tag,
  HandCoins,
  ChartLine,
  ReceiptText,
  CircleDollarSign,
  CreditCard,
  Coins,
  BadgePercent,
  Wallet,
  House,
} from "lucide-react";
import { getUserRole } from "../../Utils/Common";
import { getAllowedUrlsForRoles } from "../../Utils/AccessControl";

const Navbar: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const userRole = getUserRole() ?? "";

  const relatedRoutes = {
    expense: [
      "/expense",  
      "/purchase-request",
      "/purchase-request/add",
      "/purchase-request/edit/:id",
      "/purchase-order",
      "/purchase-order/add",
      "/purchase-order/edit/:id",
      "/payment-request",
      "/payment-request/add",
      "/payment-request/edit/:id",
    ],
    purchase_request: [
      "/purchase-request",
      "/purchase-request/add",
      "/purchase-request/edit/:id",
    ],
    purchase_order: [
      "/purchase-order",
      "/purchase-order/add",
      "/purchase-order/edit/:id",
    ],
    payment_request: [
      "/payment-request",
      "/payment-request/add",
      "/payment-request/edit",
    ],
    petty_cash: [
      "/petty-cash",
      "/petty-cash-entry",
      "/petty-cash-entry/add",
      "/petty-cash-entry/edit/:id",
      "/petty-cash-entry/view/:id",
      "/petty-cash-requisition",
    ],
    reports: [
      "/sales-report", 
      "/expense-report", 
      "/profit-and-loss-report",
      "/account-receivable-report",
      "/payment-report"
    ],
    manage: [
      "/customer",
      "/expense-type",
      "/branch",
      "/user",
    ],
    end_of_day: [
      "/end-of-day", 
      "/end-of-day/add"
    ],
    credit_billing_statement: [
      "/credit-billing-statement",
      "/credit-billing-statement/add",
    ],
    credit_billing_payment: [
      "/credit-billing-payment",
      "/credit-billing-payment/add",
    ],
    discount: [
      "/discount",
    ],
    borrowed_cash: [
      "/borrowed-cash",
    ],
    advance_payment: [
      "/advance-payment",
    ],
  };

  const allowedUrls = getAllowedUrlsForRoles(userRole);

  const navOptions = [
    {
      title: "Dashboard",
      path: "/dashboard",
      icon: <House size={20} />,
      active: currentPath === "/dashboard",
      module: "/dashboard",
    },
    {
      title: "Daily Sales Report",
      path: "/daily-sales-report",
      icon: <ChartLine size={20} />,
      active: currentPath === "/daily-sales-report",
      module: "/daily-sales-report",
    },
    {
      title: "Credit Billing Statement",
      path: "/credit-billing-statement",
      icon: <ReceiptText size={20} />,
      active: relatedRoutes.credit_billing_statement.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/credit-billing-statement",
    },
    {
      title: "Credit Billing Payment",
      path: "/credit-billing-payment",
      icon: <CreditCard size={20} />,
      active: relatedRoutes.credit_billing_payment.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/credit-billing-payment",
    },
    {
      title: "Discount",
      path: "/discount",
      icon: <BadgePercent size={20} />,
      active: relatedRoutes.discount.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/discount",
    },
    {
      title: "Borrowed Cash",
      path: "/borrowed-cash",
      icon: <HandCoins size={20} />,
      active: relatedRoutes.borrowed_cash.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/borrowed-cash",
    },
    {
      title: "Advance Payment",
      path: "/advance-payment",
      icon: <Wallet  size={20} />,
      active: relatedRoutes.advance_payment.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/advance-payment",
    },
    {
      title: "End of Day",
      path: "/end-of-day",
      icon: <CircleDollarSign size={20} />,
      active: relatedRoutes.end_of_day.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/end-of-day",
    },
    {
      title: "Petty Cash",
      path: "/petty-cash",
      icon: <Coins size={20} />,
      active: relatedRoutes.petty_cash.some((route) =>
        currentPath.startsWith(route)
      ),
      module: "/petty-cash",
      sub_menu: [
        {
          title: "Cash In",
          path: "/petty-cash-entry",
          active: currentPath === "/petty-cash-entry",
          module: "/petty-cash-entry",
        },
        {
          title: "Requisition",
          path: "/petty-cash-requisition",
          active: currentPath.startsWith("/petty-cash-requisition"),
          module: "/petty-cash-requisition",
        },
      ],
    },
    {
      title: "Expense",
      path: currentPath,
      icon: <BanknoteArrowDown size={20} />,
      active: relatedRoutes.expense.includes(currentPath),
      module: "/expense",
      sub_menu: [
        {
          title: "Purchase Request",
          path: "/purchase-request",
          active: relatedRoutes.purchase_request.some((route) =>
            currentPath.startsWith(route)
          ),
          module: "/purchase-request",
        },
        {
          title: "Purchase Order",
          path: "/purchase-order",
          active: relatedRoutes.purchase_order.some((route) =>
            currentPath.startsWith(route)
          ),
          module: "/purchase-order",
        },
        {
          title: "Payment Request",
          path: "/payment-request",
          active: relatedRoutes.payment_request.some((route) =>
            currentPath.startsWith(route)
          ),
          module: "/payment-request",
        },
      ],
    },
    {
      title: "Reports",
      path: currentPath,
      icon: <ChartArea size={20} />,
      active: relatedRoutes.reports.includes(currentPath),
      module: "/reports",
      sub_menu: [
        {
          title: "Account Recievable Report",
          path: "/account-receivable-report",
          active: currentPath === "/account-receivable-report",
          module: "/account-receivable-report",
        },
        {
          title: "Expense Report",
          path: "/expense-report",
          active: currentPath === "/expense-report",
          module: "/expense-report",
        },
        {
          title: "Payment Report",
          path: "/payment-report",
          active: currentPath === "/payment-report",
          module: "/payment-report",
        },
        {
          title: "Profit and Loss Report",
          path: "/profit-and-loss-report",
          active: currentPath === "/profit-and-loss-report",
          module: "/profit-and-loss-report",
        },
        {
          title: "Sales Report",
          path: "/sales-report",
          active: currentPath === "/sales-report",
          module: "/sales-report",
        },
      ],
    },
    {
      title: "Manage",
      path: currentPath,
      icon: <Settings size={20} />,
      active: relatedRoutes.manage.includes(currentPath),
      module: "/manage",
      sub_menu: [
        {
          title: "Branch",
          path: "/branch",
          active: currentPath === "/branch",
          module: "/branch",
        },
        {
          title: "Customer",
          path: "/customer",
          active: currentPath === "/customer",
          module: "/customer",
        },
        {
          title: "Expense Type",
          path: "/expense-type",
          active: currentPath === "/expense-type",
          module: "/expense-type",
        },
        // {
        //   title: "Test and Price",
        //   path: "/test-price",
        //   active: currentPath === "/test-price",
        //   module: "/test-price",
        // },
        {
          title: "User",
          path: "/user",
          active: currentPath === "/user",
          module: "/user",
        },
      ],
    },
  ];

  return (
    <div>
      <NavbarConf>
        {navOptions.map((option: any) => {
          return (
            allowedUrls.includes(option.module) && (
              <NavbarItem
                icon={option.icon}
                text={option.title}
                url={option.path}
                active={option.active}
                fontSize="text-md"
                // onClick={() => handleItemClick(option.path)}
                subMenu={
                  option.sub_menu ? (
                    <ul>
                      {option.sub_menu
                        .filter((submenu: any) => allowedUrls.includes(submenu.module))
                        .map((submenu: any) => {
                          return (
                            <NavbarItem
                              icon={null}
                              text={submenu?.title}
                              url={submenu.path}
                              active={submenu.active}
                              isSubMenu
                              fontSize="text-md"
                            />
                          );
                        })}
                    </ul>
                  ) : null
                }
              />
            )
          );
        })}
      </NavbarConf>
    </div>
  );
};

export default Navbar;
